import { useEffect, MutableRefObject, useRef } from 'react';
import { eventStreamService } from 'core-roblox-utilities';
import { getElementWidth, isElementInWindow } from '../utils/browserUtils';
import parsingUtils from '../utils/parsingUtils';
import { debounce } from '../utils/helperUtils';
import { TGameData } from '../types/bedev1Types';
import eventStreamConstants, { TCarouselGameImpressions } from '../constants/eventStreamConstants';

export const useGameImpressionCarouselTracker = <T extends HTMLElement, U extends HTMLElement>(
  carouselRef: MutableRefObject<T | null>,
  tileRef: MutableRefObject<U | null>,
  gameData: TGameData[] | undefined,
  metaData: Pick<
    TCarouselGameImpressions,
    'sortPos' | 'gameSetTypeId' | 'page' | 'homePageSessionInfo'
  >
): void => {
  const gameDataRef = useRef<TGameData[] | undefined>(gameData);
  const impressedRangeRef = useRef<[number, number] | undefined>(undefined);
  const calculateCarouselVisibility = (carouselElement: T | null): boolean => {
    if (carouselElement) {
      return isElementInWindow(carouselElement);
    }
    return false;
  };

  const calculateVisibleGameTiles = (
    carouselElement?: T | null,
    tileElement?: U | null
  ): [number, number] | undefined => {
    if (carouselElement && tileElement) {
      const { clientWidth: carouselDomRectWidth, scrollLeft } = carouselElement;
      const tileDomRectWidth = getElementWidth(tileElement);
      const seenStartIndex = Math.round(scrollLeft / tileDomRectWidth);
      const seenEndIndex = Math.round((scrollLeft + carouselDomRectWidth) / tileDomRectWidth) - 1;
      return [seenStartIndex, seenEndIndex];
    }
    return undefined;
  };

  const updateImpressedRange = (sortedImpressedIndexes: number[]): void => {
    if (impressedRangeRef.current === undefined) {
      impressedRangeRef.current = [
        sortedImpressedIndexes[0],
        sortedImpressedIndexes[sortedImpressedIndexes.length - 1]
      ];
    } else {
      const minRange =
        impressedRangeRef.current[0] < sortedImpressedIndexes[0]
          ? impressedRangeRef.current[0]
          : sortedImpressedIndexes[0];
      const maxRange =
        impressedRangeRef.current[1] > sortedImpressedIndexes[sortedImpressedIndexes.length - 1]
          ? impressedRangeRef.current[1]
          : sortedImpressedIndexes[sortedImpressedIndexes.length - 1];
      impressedRangeRef.current = [minRange, maxRange];
    }
  };
  const sendEvent = () => {
    // Calculate visibility
    let sortedImpressedIndexes: number[] = [];
    const isCarouselVisible = calculateCarouselVisibility(carouselRef.current);
    const possibleCarouselVisibleTileRange = calculateVisibleGameTiles(
      carouselRef.current,
      tileRef.current
    );

    // Retrieve Impressions Intersections
    if (isCarouselVisible && possibleCarouselVisibleTileRange) {
      sortedImpressedIndexes = parsingUtils.calculateImpressedIndexes(
        impressedRangeRef.current,
        possibleCarouselVisibleTileRange
      );
    }

    // Trigger Event only where there are Unseen Tiles
    if (sortedImpressedIndexes.length !== 0 && gameDataRef.current) {
      const { current } = gameDataRef;
      const parsedImpressedIndexes = sortedImpressedIndexes.filter(id => id < current.length);
      updateImpressedRange(parsedImpressedIndexes);
      const gameImpressionsParams = eventStreamConstants.gameImpressions({
        rootPlaceIds: parsedImpressedIndexes.map(index => current[index].placeId),
        universeIds: parsedImpressedIndexes.map(index => current[index].universeId),
        absPositions: parsedImpressedIndexes,
        ...metaData
      });
      eventStreamService.sendEvent(...gameImpressionsParams);
    }
  };

  const [onDebouncedEvent, cancelOnDebouncedEvent] = debounce(() => {
    sendEvent();
  });

  const onCleanUp = () => {
    cancelOnDebouncedEvent();
    window.removeEventListener('scroll', onDebouncedEvent);
    window.removeEventListener('resize', onDebouncedEvent);
  };

  useEffect(() => {
    window.addEventListener('scroll', onDebouncedEvent);
    window.addEventListener('resize', onDebouncedEvent);
    return onCleanUp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentCarouselRef = carouselRef.current;
    currentCarouselRef?.addEventListener('scroll', onDebouncedEvent);
    return () => {
      currentCarouselRef?.removeEventListener('scroll', onDebouncedEvent);
    };
  }, [carouselRef, onDebouncedEvent]);

  useEffect(() => {
    gameDataRef.current = gameData;
    sendEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameData]);
};

export default useGameImpressionCarouselTracker;
